/**
 * API client — thin wrapper around fetch with JWT auth and typed responses.
 * All methods correspond to Worker API routes defined in the design doc.
 */

import type {
  ApiResponse,
  AuthMeResponse,
  Build,
  CreateSiteRequest,
  CreateSiteResponse,
  Domain,
  DomainStatusResponse,
  Site,
  AddDomainRequest,
  UpdateSiteRequest,
  RegenerateApiKeyResponse,
} from '../types.ts';

const TOKEN_KEY = 'auth_token';

/** Base URL for the API — defaults to same origin in production. */
const getBaseUrl = (): string => {
  const envUrl = import.meta.env.VITE_API_URL;
  return envUrl || '';
};

/** Error with optional machine-readable code from the API response body. */
export class ApiError extends Error {
  code?: string;
  constructor(message: string, code?: string) {
    super(message);
    this.code = code;
  }
}

/**
 * Core fetch wrapper that attaches auth headers and handles common error cases.
 * On 401 the stored token is cleared and the user is redirected to /login.
 */
const request = async <T>(
  path: string,
  options: RequestInit = {},
): Promise<T> => {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    localStorage.removeItem(TOKEN_KEY);
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const body = await response.json().catch(() => ({})) as { error?: string; code?: string };
    throw new ApiError(
      body.error || `Request failed: ${response.status} ${response.statusText}`,
      body.code,
    );
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
};

// ---------------------------------------------------------------------------
// Auth
// ---------------------------------------------------------------------------

/** Returns the URL that starts the Google OAuth flow. */
const getGoogleLoginUrl = (): string => {
  return `${getBaseUrl()}/api/auth/google`;
};

/** Exchange the callback token — called when the user lands on /login?token=... */
const handleAuthCallback = (token: string): void => {
  localStorage.setItem(TOKEN_KEY, token);
};

/** Fetch the current user. Throws on 401. */
const getMe = async (): Promise<ApiResponse<AuthMeResponse>> => {
  return request<ApiResponse<AuthMeResponse>>('/api/auth/me');
};

/** Remove the stored JWT. */
const logout = (): void => {
  localStorage.removeItem(TOKEN_KEY);
};

/** Check whether a token exists (does not validate it). */
const hasToken = (): boolean => {
  return localStorage.getItem(TOKEN_KEY) !== null;
};

// ---------------------------------------------------------------------------
// Sites
// ---------------------------------------------------------------------------

const listSites = async (): Promise<ApiResponse<Site[]>> => {
  return request<ApiResponse<Site[]>>('/api/sites');
};

const getSite = async (id: string): Promise<ApiResponse<Site>> => {
  return request<ApiResponse<Site>>(`/api/sites/${encodeURIComponent(id)}`);
};

const createSite = async (
  body: CreateSiteRequest,
): Promise<ApiResponse<CreateSiteResponse>> => {
  return request<ApiResponse<CreateSiteResponse>>('/api/sites', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

const updateSite = async (
  id: string,
  body: UpdateSiteRequest,
): Promise<ApiResponse<Site>> => {
  return request<ApiResponse<Site>>(
    `/api/sites/${encodeURIComponent(id)}`,
    { method: 'PATCH', body: JSON.stringify(body) },
  );
};

const deleteSite = async (id: string): Promise<void> => {
  return request<void>(
    `/api/sites/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
  );
};

const regenerateApiKey = async (
  id: string,
): Promise<ApiResponse<RegenerateApiKeyResponse>> => {
  return request<ApiResponse<RegenerateApiKeyResponse>>(
    `/api/sites/${encodeURIComponent(id)}/api-key`,
    { method: 'POST' },
  );
};

const publishSite = async (id: string): Promise<ApiResponse<Build>> => {
  return request<ApiResponse<Build>>(
    `/api/sites/${encodeURIComponent(id)}/publish`,
    { method: 'POST' },
  );
};

// ---------------------------------------------------------------------------
// Builds
// ---------------------------------------------------------------------------

const listBuilds = async (siteId: string): Promise<ApiResponse<Build[]>> => {
  return request<ApiResponse<Build[]>>(
    `/api/sites/${encodeURIComponent(siteId)}/builds`,
  );
};

// ---------------------------------------------------------------------------
// Domains
// ---------------------------------------------------------------------------

const listDomains = async (): Promise<ApiResponse<Domain[]>> => {
  return request<ApiResponse<Domain[]>>('/api/domains');
};

const addDomain = async (
  body: AddDomainRequest,
): Promise<ApiResponse<Domain>> => {
  return request<ApiResponse<Domain>>('/api/domains', {
    method: 'POST',
    body: JSON.stringify(body),
  });
};

const getDomainStatus = async (
  hostname: string,
): Promise<ApiResponse<DomainStatusResponse>> => {
  return request<ApiResponse<DomainStatusResponse>>(
    `/api/domains/${encodeURIComponent(hostname)}/status`,
  );
};

const removeDomain = async (hostname: string): Promise<void> => {
  return request<void>(
    `/api/domains/${encodeURIComponent(hostname)}`,
    { method: 'DELETE' },
  );
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

export const api = {
  auth: {
    getGoogleLoginUrl,
    handleAuthCallback,
    getMe,
    logout,
    hasToken,
  },
  sites: {
    list: listSites,
    get: getSite,
    create: createSite,
    update: updateSite,
    delete: deleteSite,
    regenerateApiKey,
    publish: publishSite,
  },
  builds: {
    list: listBuilds,
  },
  domains: {
    list: listDomains,
    add: addDomain,
    getStatus: getDomainStatus,
    remove: removeDomain,
  },
} as const;
